import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ModalPriorityService } from './modal-priority.service';
import { BroadcastChannelService } from './broadcast-channel.service';

export interface TokenExpirationState {
  isOpen: boolean;
  timeRemaining: number; // seconds
  sessionExpired: boolean; // true when timer finished and showing session expired message
}

@Injectable({
  providedIn: 'root'
})
export class TokenExpirationService implements OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  private modalPriorityService = inject(ModalPriorityService);
  private broadcastChannelService = inject(BroadcastChannelService);
  private checkInterval: Subscription | null = null;
  private countdownInterval: Subscription | null = null;
  private prioritySubscription?: Subscription;
  private broadcastSubscription?: Subscription;
  private destroy$ = new Subject<void>();

  static readonly WARNING_DURATION_STORAGE_KEY = 'auth_warning_duration_ms';
  static readonly WARNING_DURATION_FALLBACK_MS = 300000;

  /** Warning duration in ms. From backend (login/refresh/2fa) when set; otherwise fallback. */
  getWarningDurationMs(): number {
    const raw = localStorage.getItem(TokenExpirationService.WARNING_DURATION_STORAGE_KEY);
    if (raw == null || raw === '') return TokenExpirationService.WARNING_DURATION_FALLBACK_MS;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : TokenExpirationService.WARNING_DURATION_FALLBACK_MS;
  }

  getWarningDurationSeconds(): number {
    return Math.floor(this.getWarningDurationMs() / 1000);
  }

  private modalStateSubject = new BehaviorSubject<TokenExpirationState>({
    isOpen: false,
    timeRemaining: Math.floor(TokenExpirationService.WARNING_DURATION_FALLBACK_MS / 1000),
    sessionExpired: false
  });

  public modalState$: Observable<TokenExpirationState> = this.modalStateSubject.asObservable();

  /**
   * Start tracking token expiration.
   * Only runs when a token exists (after login or 2FA verify). No-op if no token.
   * Checks every 60 seconds if token is approaching expiry.
   */
  startTracking(): void {
    if (!this.getExpiryTimestamp()) {
      return; // No token (e.g. on 2FA step); do not start timer.
    }
    this.stopTracking(); // Clear any existing interval
    
    // Listen to priority changes - close modal if higher priority appears
    this.prioritySubscription = this.modalPriorityService.priority$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(priority => {
      if (priority !== 'token-expiration' && priority !== null && this.modalStateSubject.value.isOpen) {
        // Higher priority modal is showing, close token expiration modal
        this.hideModal();
      }
    });
    
    // Listen to BroadcastChannel messages for cross-tab sync
    this.broadcastSubscription = this.broadcastChannelService.messages$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(message => {
      switch (message.type) {
        case this.broadcastChannelService.MESSAGE_TYPES.TOKEN_REFRESHED:
          // Token was refreshed in another tab - update our tracking
          this.hideModal();
          // Re-check with new expiry (will be read from localStorage)
          setTimeout(() => {
            try {
              this.checkTokenExpiry();
            } catch (error) {
              console.error('Error checking token expiry after refresh:', error);
            }
          }, 100);
          break;
          
        case this.broadcastChannelService.MESSAGE_TYPES.ACCOUNT_DISABLED:
        case this.broadcastChannelService.MESSAGE_TYPES.SESSION_REVOKED:
          // Account disabled/revoked in another tab - stop tracking
          this.stopTracking();
          this.hideModal();
          break;
          
        case this.broadcastChannelService.MESSAGE_TYPES.LOGOUT:
          // Logged out in another tab
          this.stopTracking();
          break;
          
        case this.broadcastChannelService.MESSAGE_TYPES.TOKEN_EXPIRED:
          // Token expired in another tab
          this.handleTokenExpired();
          break;
      }
    });
    
    // Background check every 60 seconds with error handling
    this.checkInterval = interval(60000).subscribe({
      next: () => {
        try {
          this.checkTokenExpiry();
        } catch (error) {
          console.error('Error checking token expiry:', error);
          // Stop tracking on persistent errors
          this.stopTracking();
        }
      },
      error: (err) => {
        console.error('Token expiry check interval error:', err);
        this.stopTracking();
      }
    });
    
    // Also check immediately
    try {
      this.checkTokenExpiry();
    } catch (error) {
      console.error('Error on initial token expiry check:', error);
    }
  }

  /**
   * Stop tracking token expiration
   */
  stopTracking(): void {
    if (this.checkInterval) {
      this.checkInterval.unsubscribe();
      this.checkInterval = null;
    }
    this.stopCountdown();
    this.hideModal();
  }

  /**
   * Check if token is expiring soon (within configured warning duration)
   */
  private checkTokenExpiry(): void {
    const expiryTimestamp = this.getExpiryTimestamp();
    if (!expiryTimestamp) {
      return; // No token stored
    }

    // Check if higher priority modal is showing - don't show token expiration if so
    const currentPriority = this.modalPriorityService.getCurrentPriority();
    if (currentPriority === 'account-disabled' || currentPriority === 'session-revoked') {
      // Don't show token expiration if account is disabled/revoked
      this.hideModal();
      return;
    }

    const now = Date.now();
    const timeRemaining = expiryTimestamp - now;

    // If token already expired, redirect immediately
    if (timeRemaining <= 0) {
      this.handleTokenExpired();
      return;
    }

    // If within warning duration of expiry, show modal
    if (timeRemaining <= this.getWarningDurationMs() && !this.modalStateSubject.value.isOpen) {
      this.showModal(timeRemaining);
    }
  }

  /**
   * Show warning modal and start countdown
   */
  private showModal(timeRemainingMs: number): void {
    // Request permission from priority service
    // This will only succeed if no higher priority modals are showing
    this.modalPriorityService.showTokenExpiration();
    
    // Double-check priority before showing (in case priority changed between check and show)
    const currentPriority = this.modalPriorityService.getCurrentPriority();
    if (currentPriority !== 'token-expiration') {
      return; // Higher priority modal is showing, don't show token expiration
    }
    
    const timeRemainingSeconds = Math.floor(timeRemainingMs / 1000);
    this.modalStateSubject.next({
      isOpen: true,
      timeRemaining: Math.min(timeRemainingSeconds, this.getWarningDurationSeconds()),
      sessionExpired: false
    });
    
    this.startCountdown();
  }

  /**
   * Hide warning modal
   */
  hideModal(): void {
    this.stopCountdown();
    this.modalStateSubject.next({
      isOpen: false,
      timeRemaining: this.getWarningDurationSeconds(),
      sessionExpired: false
    });
    // Clear priority if this was the active modal
    if (this.modalPriorityService.getCurrentPriority() === 'token-expiration') {
      this.modalPriorityService.clear();
    }
  }

  /**
   * Show session expired modal (called after redirect to login page)
   */
  showSessionExpiredModal(): void {
    this.modalStateSubject.next({
      isOpen: true,
      timeRemaining: 0,
      sessionExpired: true
    });
  }

  /**
   * Start countdown timer (updates every second)
   */
  private startCountdown(): void {
    this.stopCountdown();
    
    this.countdownInterval = interval(1000).subscribe(() => {
      const current = this.modalStateSubject.value;
      if (current.timeRemaining <= 0) {
        this.handleTokenExpired();
        return;
      }
      
      this.modalStateSubject.next({
        ...current,
        timeRemaining: current.timeRemaining - 1
      });
    });
  }

  /**
   * Stop countdown timer
   * Made public so AppComponent can stop it when "Continue Session" is clicked
   * This prevents race condition where timer hits 0 while refresh is in progress
   */
  stopCountdown(): void {
    if (this.countdownInterval) {
      this.countdownInterval.unsubscribe();
      this.countdownInterval = null;
    }
  }

  /**
   * Handle token expiration - redirect to login first, then show session expired message
   */
  private handleTokenExpired(): void {
    this.stopCountdown();
    this.stopTracking();
    this.authService.clearAuthStorage();
    
    // Redirect to login page with query parameter - sign-in component will show the modal
    this.router.navigate(['/signin'], { queryParams: { sessionExpired: 'true' } });
  }

  /**
   * Get token expiry timestamp from localStorage
   * Uses environment.auth.expiryKey for consistency (not hardcoded)
   */
  private getExpiryTimestamp(): number | null {
    const expiryKey = environment.auth.expiryKey;
    const expiry = localStorage.getItem(expiryKey);
    if (!expiry) {
      return null;
    }
    const timestamp = Number(expiry);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  /**
   * Format time remaining as MM:SS
   */
  formatTimeRemaining(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  /**
   * Notify other tabs that token was refreshed
   * Called from AppComponent after successful token refresh
   */
  notifyTokenRefreshed(): void {
    const expiryTimestamp = this.getExpiryTimestamp();
    this.broadcastChannelService.postMessage(
      this.broadcastChannelService.MESSAGE_TYPES.TOKEN_REFRESHED,
      { expiryTimestamp }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.prioritySubscription?.unsubscribe();
    this.broadcastSubscription?.unsubscribe();
    this.stopTracking();
  }
}
